import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  BrandConfig,
  loadBrandConfig,
  defaultBrandConfig,
  SELECTED_BRAND,
} from "../config/brandConfig";

interface BrandContextType {
  brandConfig: BrandConfig;
  isLoading: boolean;
  error: string | null;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

interface BrandProviderProps {
  children: ReactNode;
}

// System fonts that don't need to be loaded from Google
const SYSTEM_FONTS = [
  "arial",
  "helvetica",
  "sans-serif",
  "serif",
  "monospace",
  "helvetica neue",
];

// Function to dynamically load Google Fonts
const loadGoogleFont = (fontFamily: string): Promise<void> => {
  return new Promise((resolve) => {
    // Extract just the first font from a font stack (e.g., "'Poppins', Arial, sans-serif" -> "Poppins")
    const firstFont = fontFamily.split(",")[0].trim().replace(/['"]/g, "");

    // Skip system fonts
    if (SYSTEM_FONTS.includes(firstFont.toLowerCase())) {
      resolve();
      return;
    }

    // Skip if font is already loaded
    const fontId = firstFont.toLowerCase().replace(/\s+/g, "-");
    if (document.querySelector(`link[data-font-id="${fontId}"]`)) {
      resolve();
      return;
    }

    // Create link element for Google Fonts
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${firstFont.replace(/\s+/g, "+")}:wght@300;400;500;600;700&display=swap`;
    link.setAttribute("data-font-id", fontId);

    link.onload = () => {
      console.log(`✅ Loaded Google Font: ${firstFont}`);
      resolve();
    };
    link.onerror = () => {
      // Don't fail - just log and continue (font may not be on Google Fonts)
      console.warn(
        `⚠️ Font not available on Google Fonts: ${firstFont}, using system fallback`,
      );
      resolve(); // Resolve anyway - use system fallback fonts
    };

    // Add to document head
    document.head.appendChild(link);
  });
};

export const BrandProvider: React.FC<BrandProviderProps> = ({ children }) => {
  const [brandConfig, setBrandConfig] =
    useState<BrandConfig>(defaultBrandConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track current brand to detect changes
  const [currentBrand, setCurrentBrand] = useState<string>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("brand") || SELECTED_BRAND || "default";
  });

  // Load dark mode from localStorage on initialization
  const getInitialDarkMode = (): boolean => {
    try {
      const savedDarkMode = localStorage.getItem("kiosk-dark-mode");
      return savedDarkMode ? JSON.parse(savedDarkMode) : false;
    } catch (error) {
      console.error("Failed to load dark mode from localStorage:", error);
      return false;
    }
  };

  const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);

  // Listen for URL changes (popstate for back/forward, custom event for programmatic navigation)
  useEffect(() => {
    const checkBrandChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const brandFromUrl = urlParams.get("brand") || SELECTED_BRAND || "default";
      if (brandFromUrl !== currentBrand) {
        setCurrentBrand(brandFromUrl);
      }
    };

    // Listen for browser back/forward navigation
    window.addEventListener("popstate", checkBrandChange);

    // Check periodically for programmatic navigation (React Router doesn't trigger popstate)
    const interval = setInterval(checkBrandChange, 100);

    return () => {
      window.removeEventListener("popstate", checkBrandChange);
      clearInterval(interval);
    };
  }, [currentBrand]);

  // Load brand config when currentBrand changes
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const config = await loadBrandConfig(currentBrand);

        // Load Google Fonts for primary, secondary, and button fonts
        await Promise.all([
          loadGoogleFont(config.typography.primary),
          loadGoogleFont(config.typography.secondary),
          loadGoogleFont(config.typography.button),
        ]);

        setBrandConfig(config);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to load brand configuration";
        setError(errorMessage);
        console.error("Brand configuration error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, [currentBrand]);

  // Save dark mode to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem("kiosk-dark-mode", JSON.stringify(isDarkMode));
    } catch (error) {
      console.error("Failed to save dark mode to localStorage:", error);
    }
  }, [isDarkMode]);

  // Apply CSS custom properties when brand config changes
  useEffect(() => {
    if (brandConfig) {
      // Load fonts if they change
      loadGoogleFont(brandConfig.typography.primary).catch(console.error);
      loadGoogleFont(brandConfig.typography.secondary).catch(console.error);
      loadGoogleFont(brandConfig.typography.button).catch(console.error);

      const root = document.documentElement;
      const mode = isDarkMode ? "dark" : "light";

      // Set color variables
      root.style.setProperty(
        "--brand-primary",
        brandConfig.colors.primary[mode],
      );
      root.style.setProperty(
        "--brand-secondary",
        brandConfig.colors.secondary[mode],
      );
      root.style.setProperty(
        "--brand-tertiary",
        brandConfig.colors.tertiary[mode],
      );
      root.style.setProperty(
        "--brand-background",
        brandConfig.colors.background[mode],
      );
      root.style.setProperty(
        "--brand-surface",
        brandConfig.colors.surface[mode],
      );
      root.style.setProperty(
        "--brand-text-primary",
        brandConfig.colors.text.primary[mode],
      );
      root.style.setProperty(
        "--brand-text-secondary",
        brandConfig.colors.text.secondary[mode],
      );
      root.style.setProperty(
        "--brand-text-muted",
        brandConfig.colors.text.muted[mode],
      );
      root.style.setProperty("--brand-border", brandConfig.colors.border[mode]);
      root.style.setProperty("--brand-accent", brandConfig.colors.accent[mode]);

      // Set typography variables
      root.style.setProperty(
        "--brand-font-primary",
        brandConfig.typography.primary,
      );
      root.style.setProperty(
        "--brand-font-secondary",
        brandConfig.typography.secondary,
      );
      root.style.setProperty("--brand-font-body", brandConfig.typography.body);
      root.style.setProperty(
        "--brand-font-button",
        brandConfig.typography.button,
      );
      root.style.setProperty(
        "--brand-font-weight-light",
        brandConfig.typography.weights.light.toString(),
      );
      root.style.setProperty(
        "--brand-font-weight-normal",
        brandConfig.typography.weights.normal.toString(),
      );
      root.style.setProperty(
        "--brand-font-weight-medium",
        brandConfig.typography.weights.medium.toString(),
      );
      root.style.setProperty(
        "--brand-font-weight-semibold",
        brandConfig.typography.weights.semibold.toString(),
      );
      root.style.setProperty(
        "--brand-font-weight-bold",
        brandConfig.typography.weights.bold.toString(),
      );

      // Set font size variables
      root.style.setProperty(
        "--brand-font-size-heading-title",
        brandConfig.typography.sizes.heading.title,
      );
      root.style.setProperty(
        "--brand-font-size-heading-subtitle",
        brandConfig.typography.sizes.heading.subtitle,
      );

      // Set button style variables
      root.style.setProperty(
        "--brand-button-text-transform",
        brandConfig.typography.buttonStyle.textTransform,
      );
      root.style.setProperty(
        "--brand-button-letter-spacing",
        brandConfig.typography.buttonStyle.letterSpacing,
      );
      root.style.setProperty(
        "--brand-button-color",
        brandConfig.typography.buttonStyle.color,
      );
      root.style.setProperty(
        "--brand-button-secondary-color",
        brandConfig.typography.buttonStyle.secondaryColor,
      );

      // Set border radius variables
      root.style.setProperty(
        "--brand-border-radius-button",
        brandConfig.borderRadius.button,
      );
    }
  }, [brandConfig, isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  const value: BrandContextType = {
    brandConfig,
    isLoading,
    error,
    isDarkMode,
    toggleDarkMode,
  };

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
};

export const useBrand = (): BrandContextType => {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error("useBrand must be used within a BrandProvider");
  }
  return context;
};

export default BrandContext;
